import React, { useState } from 'react';
import { Box, Typography, IconButton } from '@mui/material';
import makeStyles from '@mui/styles/makeStyles';
import CloseIcon from '@mui/icons-material/Close';

import TextField from 'components/CustomFields';
import CustomButton from 'components/CustomButton';
import useResumeStore from './store';

const useStyles = makeStyles((theme) => ({
  container: {
    display: 'flex',
    flexDirection: 'column',
    width: '100%',
    overflow: 'hidden',
  },
  content: {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(4),
    padding: theme.spacing(4, 6),
    overflowY: 'auto',
    flex: 1,
    '&::-webkit-scrollbar': {
      width: '6px',
    },
    '&::-webkit-scrollbar-thumb': {
      backgroundColor: theme.palette.neutral['clr-300'],
      borderRadius: '3px',
    },
  },
  categoryItem: {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(2),
    padding: theme.spacing(3, 4),
    border: `1px solid ${theme.palette.neutral['clr-300']}`,
    borderRadius: theme.spacing(1),
  },
  inputRow: {
    display: 'flex',
    gap: theme.spacing(2),
    alignItems: 'flex-end',
  },
  chipContainer: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: theme.spacing(2),
  },
  chip: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1),
    padding: theme.spacing(1, 2, 1, 3),
    borderRadius: '20px',
    backgroundColor: theme.palette.primary['clr-50'],
    border: `1px solid ${theme.palette.primary.main}`,
  },
  footer: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: theme.spacing(4, 6),
    borderTop: `1px solid ${theme.palette.neutral['clr-200']}`,
  },
}));

const categories = [
  { key: 'skills', label: 'Skills' },
  { key: 'frameworks', label: 'Frameworks' },
  { key: 'tools', label: 'Tools' },
  { key: 'languages', label: 'Languages' },
];

const SkillCategory = ({ categoryKey, label, data, onAdd, onRemove }) => {
  const classes = useStyles();
  const [inputValue, setInputValue] = useState('');

  const handleAdd = () => {
    const trimmed = inputValue.trim();
    if (trimmed) {
      onAdd(categoryKey, trimmed);
      setInputValue('');
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleAdd();
    }
  };

  return (
    <Box className={classes.categoryItem}>
      <Box className={classes.inputRow}>
        <Box flex={1}>
          <TextField
            label={label}
            placeholder={`Add a ${label.toLowerCase().slice(0, -1)}...`}
            value={inputValue}
            onChange={(e) => setInputValue(e.target.value)}
            onKeyDown={handleKeyDown}
            required
          />
        </Box>
        <CustomButton
          size='small'
          onClick={handleAdd}
          disabled={!inputValue.trim()}
          sx={{ width: 'fit-content', height: '40px' }}
        >
          Add
        </CustomButton>
      </Box>

      {data.items.length > 0 && (
        <Box className={classes.chipContainer}>
          {data.items.map((item, index) => (
            <Box key={index} className={classes.chip}>
              <Typography variant='body02-bold' color='primary.main'>
                {item}
              </Typography>
              <IconButton
                size='small'
                onClick={() => onRemove(categoryKey, index)}
                sx={{ padding: 0 }}
              >
                <CloseIcon sx={{ fontSize: '16px', color: 'primary.main' }} />
              </IconButton>
            </Box>
          ))}
        </Box>
      )}
    </Box>
  );
};

const SkillsForm = ({ onNext }) => {
  const classes = useStyles();

  const [skills, addSkillItem, removeSkillItem] = useResumeStore(
    (state) => [
      state.skills, state.addSkillItem, state.removeSkillItem,
    ]
  );

  const isNextDisabled =
    skills.skills.items.length === 0 ||
    skills.tools.items.length === 0 ||
    skills.languages.items.length === 0;

  return (
    <Box className={classes.container}>
      <Box className={classes.content}>
        {categories.map((category) => (
          <SkillCategory
            key={category.key}
            categoryKey={category.key}
            label={category.label}
            data={skills[category.key]}
            onAdd={addSkillItem}
            onRemove={removeSkillItem}
          />
        ))}
      </Box>

      <Box className={classes.footer}>
        <Typography variant='body02-bold' color='warning.main'>
          Note: Your data will be lost if you leave this page.
        </Typography>
        <CustomButton
          size='medium'
          onClick={onNext}
          disabled={isNextDisabled}
          sx={{ width: 'fit-content' }}
        >
          Next
        </CustomButton>
      </Box>
    </Box>
  );
};

export default SkillsForm;
